/**
 * THE PLACE CARD (B8) — design-spec §A (geometry), §C (anatomy and copy),
 * §E.1–E.3 (enter at Tarr − 120, exit 294, A → B cross-fade, prev/next slide),
 * §E.6 (reduced motion: the route, road by road), §H (region, focus, Esc).
 *
 * Desktop and kiosk: a glass <aside> on the right. Mobile (or `embedded`): the
 * content only, for B10's bottom sheet, which is the surface.
 * Nothing animates through React state: enters and exits are CSS, the strip
 * reads route:progress into a CSS variable, the height follows by WAAPI.
 */
import "./card.css";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import type { PlaceCardProps } from "@/contracts/ui";
import { EASE_CSS, GlassPanel, IconButton, cx, useReducedMotion } from "@/design";
import { announce } from "@/store/bus";
import { actions, appStore, useApp } from "@/store/store";
import { CARD_INITIAL, IN_MS, LEAVE_MS, OUT_MS, REDUCED_MS, cardReduce, dropOut, finishLeave, type Dir } from "./cardState";
import { getDriveStarter, setDriveStarter } from "./driveState";
import { shareText, shareUrl } from "./format";
import { useDriveHold, useSequence } from "./hooks";
import { CardBody, CardFoot, CardHead, useEscape } from "./parts";
import { getDockTarget, setDockTarget } from "./sequence";
import { ToastHost, showToast } from "./Toast";
import { buildView, positionOf, stripGeometry } from "./view";

export interface PlaceCardOwnProps extends PlaceCardProps {
  /** Render the content only, for the mobile bottom sheet. Default: layout === "mobile". */
  embedded?: boolean;
  /** §C.9: fit the place's extra pins on the map (easeTo 900 drift) and pulse them. Without it the note is plain text. */
  onShowExtraPins?: (placeId: string) => void;
}

type CSSVars = CSSProperties & { [k: `--${string}`]: string | number | undefined };

const SLIDE = 28;
const slideIn = (dir: Dir): CSSVars | undefined => (dir ? { "--sx": `${dir * SLIDE}px` } : undefined);
const slideOut = (dir: Dir): CSSVars | undefined => (dir ? { "--sxo": `${-dir * SLIDE}px` } : undefined);

const TURNS_ID = "ld-card-turns";

export function PlaceCard({ onDrive, onClose, embedded: embeddedProp, onShowExtraPins }: PlaceCardOwnProps) {
  const seq = useSequence();
  const phase = useApp((s) => s.phase);
  const layout = useApp((s) => s.layout);
  const kiosk = useApp((s) => s.kiosk) || layout === "kiosk";
  const sheet = useApp((s) => s.sheet);
  const data = useApp((s) => s.data);
  const routes = useApp((s) => s.routes);
  const geo = useApp((s) => s.geo);
  const posKey = useApp((s) => {
    const p = positionOf(s, s.selectedId);
    return p ? `${p.i}/${p.n}` : "";
  });
  const reduced = useReducedMotion();
  const hold = useDriveHold();
  const embedded = embeddedProp ?? layout === "mobile";
  const suppressed = !embedded && (phase === "drive" || phase === "loading" || phase === "intro" || hold);

  /* The HUD's "Resume" starts the drive the way this card does. */
  useEffect(() => {
    setDriveStarter(onDrive);
    return () => {
      if (getDriveStarter() === onDrive) setDriveStarter(null);
    };
  }, [onDrive]);

  /* ── state machine ── */
  const [st, setSt] = useState(CARD_INITIAL);
  const navDir = useRef<Dir>(0);
  const docked = seq.phase === "docked";
  useLayoutEffect(() => {
    const dir = navDir.current;
    setSt((s) => cardReduce(s, { id: seq.id, docked, drew: seq.drew, suppressed, embedded, dir }));
  }, [seq.id, seq.seq, docked, seq.drew, suppressed, embedded]);
  useEffect(() => {
    navDir.current = 0;
  }, [seq.id]);

  const outKey = st.out?.key;
  useEffect(() => {
    if (outKey == null) return undefined;
    const t = setTimeout(() => setSt((s) => dropOut(s, outKey)), reduced ? REDUCED_MS : OUT_MS + 24);
    return () => clearTimeout(t);
  }, [outKey, reduced]);
  useEffect(() => {
    if (st.mode !== "leaving") return undefined;
    const t = setTimeout(() => setSt(finishLeave), reduced ? REDUCED_MS : LEAVE_MS);
    return () => clearTimeout(t);
  }, [st.mode, reduced]);

  /* the quiet body folds away once it has faded, so the card settles to the head (§E.3) */
  const [folded, setFolded] = useState(false);
  useEffect(() => {
    if (!st.pending) {
      setFolded(false);
      return undefined;
    }
    const t = setTimeout(() => setFolded(true), reduced ? REDUCED_MS : OUT_MS);
    return () => clearTimeout(t);
  }, [st.pending, reduced]);

  /* ── views (data is set once; ids change on selection) ── */
  const headId = st.head?.id ?? null;
  const outId = st.out?.id ?? null;
  const bodyId = st.body?.id ?? null;
  const headView = useMemo(() => (data ? buildView({ ...appStore.getState(), data, routes, geo }, headId) : null), [headId, data, routes, geo]);
  const outView = useMemo(() => (data ? buildView({ ...appStore.getState(), data, routes, geo }, outId) : null), [outId, data, routes, geo]);
  const bodyView = useMemo(() => (data ? buildView({ ...appStore.getState(), data, routes, geo }, bodyId) : null), [bodyId, data, routes, geo]);
  const pos = useMemo(() => {
    const [i, n] = posKey.split("/").map(Number);
    return posKey ? { i, n } : null;
  }, [posKey]);

  /* ── reduced motion: a 160 ms fade instead of the flight ── */
  const [lit, setLit] = useState(false);
  useLayoutEffect(() => {
    setLit(false);
    if (st.mode !== "open") return undefined;
    const r = requestAnimationFrame(() => setLit(true));
    return () => cancelAnimationFrame(r);
  }, [st.mount, st.mode]);

  /* ── the height follows the content (A → B), WAAPI, 240 settle ──
     Measured by a ResizeObserver (after layout, before paint), never read in a React commit: the card
     mounts at the dock and exits at a drive's start, and a layout read there would force a full style
     and layout pass in the same task (QA L2-06, L2-07). A content change arms the next size report. */
  const panelRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastH = useRef(0);
  const armed = useRef<number | null>(null);
  const hAnim = useRef<Animation | null>(null);
  const reducedRef = useRef(reduced);
  reducedRef.current = reduced;
  const open = st.mode === "open";
  useEffect(() => {
    const el = panelRef.current;
    lastH.current = 0;
    armed.current = null;
    if (!el || embedded || !open || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => {
      const box = entries[entries.length - 1]?.borderBoxSize?.[0];
      const h = box ? box.blockSize : el.offsetHeight;
      const from = armed.current;
      armed.current = null;
      if (from != null && !reducedRef.current && Math.abs(from - h) >= 2 && typeof el.animate === "function") {
        hAnim.current = el.animate([{ height: `${from}px` }, { height: `${h}px` }], { duration: IN_MS, easing: EASE_CSS.settle });
      }
      lastH.current = h;
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      hAnim.current?.cancel();
      hAnim.current = null;
    };
  }, [embedded, open, st.mount]);
  const headKey = st.head?.key;
  const bodyKey = st.body?.key;
  const pendingNow = st.pending;
  useLayoutEffect(() => {
    // the content changed: the next size report grows or shrinks from the height on screen now
    if (!open || embedded || !lastH.current) return undefined;
    armed.current = lastH.current;
    hAnim.current?.cancel();
    hAnim.current = null;
    // a change that kept the size leaves nothing armed for an unrelated resize later
    let r2 = 0;
    const r1 = requestAnimationFrame(() => {
      r2 = requestAnimationFrame(() => {
        armed.current = null;
      });
    });
    return () => {
      cancelAnimationFrame(r1);
      cancelAnimationFrame(r2);
    };
  }, [headKey, bodyKey, pendingNow, folded, open, embedded]);

  /* a new place starts at the top (a write only when the body has been scrolled: no layout otherwise) */
  const scrolled = useRef(false);
  useEffect(() => {
    const sc = scrollRef.current;
    scrolled.current = false;
    if (!sc) return undefined;
    const onScroll = () => {
      scrolled.current = true;
    };
    sc.addEventListener("scroll", onScroll, { passive: true });
    return () => sc.removeEventListener("scroll", onScroll);
  }, [st.mount, open]);
  useLayoutEffect(() => {
    const sc = scrollRef.current;
    if (!sc || !scrolled.current) return;
    scrolled.current = false;
    sc.scrollTop = 0;
  }, [headKey]);

  /* ── focus: a keyboard selection on a pin lands on the heading (§H.1) ── */
  const headingRef = useRef<HTMLHeadingElement>(null);
  const focusedSeq = useRef(-1);
  useEffect(() => {
    if (!seq.focusHeading || st.mode !== "open" || headId !== seq.id || focusedSeq.current === seq.seq) return;
    focusedSeq.current = seq.seq;
    headingRef.current?.focus({ preventScroll: true });
  }, [seq.focusHeading, seq.seq, seq.id, st.mode, headId]);

  /* ── Esc closes (the drive HUD owns Esc during a drive) ── */
  useEscape(st.mode === "open" && phase !== "drive", onClose);

  /* ── the meter lands on the Facts' km figure ── */
  const onDockFig = useCallback((el: HTMLElement | null, prev?: HTMLElement | null) => {
    if (el) setDockTarget({ fig: el, card: null });
    else if (prev && getDockTarget()?.fig === prev) setDockTarget(null);
  }, []);

  /* ── reduced motion's drive: the turn list ── */
  const [turnsOpen, setTurnsOpen] = useState(false);
  const turnsRef = useRef<HTMLHeadingElement>(null);
  const wantTurnsFocus = useRef(false);
  useEffect(() => {
    setTurnsOpen(false);
  }, [bodyId]);
  useEffect(() => {
    if (!turnsOpen || !wantTurnsFocus.current) return;
    wantTurnsFocus.current = false;
    turnsRef.current?.focus({ preventScroll: true });
    // the whole list, not just its title (nearest keeps the top in view when it is taller)
    turnsRef.current?.closest("section")?.scrollIntoView({ block: "nearest", behavior: "auto" });
  }, [turnsOpen]);
  const onTurns = useCallback(() => {
    setTurnsOpen((o) => {
      wantTurnsFocus.current = !o;
      return !o;
    });
  }, []);

  /* ── share (§F share.*) ── */
  const onShare = useCallback(async () => {
    const v = headView;
    if (!v) return;
    const url = shareUrl(v.place.id, window.location);
    const { title, text } = shareText(v.place);
    if (layout === "mobile" && typeof navigator.share === "function") {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return;
      }
    }
    try {
      if (!navigator.clipboard?.writeText) throw new Error("no clipboard");
      await navigator.clipboard.writeText(url);
      showToast({ id: "share", text: "Link copied", icon: "check", ms: 3200 });
      announce("Link copied.");
    } catch {
      showToast({ id: "share", text: "The link couldn't be copied. Select it below.", icon: "share", link: url, ms: 9000 });
    }
  }, [headView, layout]);

  const onStep = useCallback((dir: 1 | -1) => {
    navDir.current = dir;
    actions.step(dir);
  }, []);

  const onExtra = useMemo(() => (onShowExtraPins && bodyId ? () => onShowExtraPins(bodyId) : undefined), [onShowExtraPins, bodyId]);

  if (st.mode === "hidden" || !st.head || !headView) return <ToastHost />;

  const live = seq.phase === "drawing" && seq.id === headId;
  // no layout read: the strip's track comes from the card's CSS width for this layout
  const strip = stripGeometry({ layout, kiosk, embedded, vw: typeof window !== "undefined" ? window.innerWidth : 1440 });
  const head = (
    <div className="ld-loc-card-stack">
      {st.out && outView && (
        <CardHead key={st.out.key} view={outView} current={false} live={false} strip={strip} className="ld-loc-card-xout" style={slideOut(st.head.dir)} />
      )}
      <CardHead
        key={st.head.key}
        view={headView}
        current
        live={live}
        strip={strip}
        headingRef={headingRef}
        className={st.head.swap ? "ld-loc-card-xin" : undefined}
        style={slideIn(st.head.dir)}
      />
    </div>
  );
  const body = st.body && bodyView && (
    <div
      key={st.body.key}
      className={cx("ld-loc-card-body ld-loc-card-main", st.pending && "is-pending", st.body.swap && "ld-loc-card-xin")}
      style={slideIn(st.body.dir)}
      aria-hidden={st.pending || undefined}
      hidden={(st.pending && folded) || undefined}
    >
      <CardBody
        view={bodyView}
        landing={!!st.body.land && !reduced}
        turnsOpen={turnsOpen && reduced}
        turnsId={TURNS_ID}
        turnsRef={turnsRef}
        onExtra={onExtra}
        onDockFig={onDockFig}
      />
    </div>
  );
  const foot = (
    <CardFoot
      view={headView}
      kiosk={kiosk}
      reduced={reduced}
      turnsOpen={turnsOpen}
      turnsId={TURNS_ID}
      pos={pos}
      onDrive={onDrive}
      onTurns={onTurns}
      onShare={onShare}
      onStep={onStep}
    />
  );
  const close = (
    <IconButton className="ld-loc-card-close" icon="close" label="Close details" solo size={kiosk && !embedded ? 56 : 36} iconSize={kiosk && !embedded ? 22 : 18} tip={null} onClick={onClose} />
  );
  const bare = !headView.strip;

  // §H.2: a region named by its heading (the place's name)
  if (embedded) {
    return (
      <>
        <section id="ld-card" role="region" aria-labelledby="ld-card-title" className={cx("ld-loc-card ld-loc-card--embedded", sheet === "peek" && "is-peek", bare && "is-bare")}>
          {close}
          <div className="ld-loc-card-scroll">
            {head}
            {body}
          </div>
          {foot}
        </section>
        <ToastHost />
      </>
    );
  }

  const leaving = st.mode === "leaving";
  return (
    <>
      <GlassPanel
        key={st.mount}
        ref={panelRef}
        as="section"
        id="ld-card"
        role="region"
        aria-labelledby="ld-card-title"
        surface="glass"
        enter={reduced ? null : "right"}
        leaving={leaving && !reduced}
        inert={leaving || undefined}
        className={cx(
          "ld-loc-card",
          kiosk && "ld-loc-card--kiosk",
          bare && "is-bare",
          leaving && "is-leaving",
          reduced && "ld-loc-card-fade",
          reduced && lit && !leaving && "is-on",
        )}
      >
        {close}
        <div ref={scrollRef} className="ld-panel__scroll ld-loc-card-scroll">
          {head}
          {body}
        </div>
        {foot}
      </GlassPanel>
      <ToastHost />
    </>
  );
}
